export interface FinanceSalesValues {
  loadedQuantities?: number;
  paidLoadedQuantities?: number;
  freePromotionalQuantities?: number;
  totalReturnedQuantities?: number;
  totalPaidReturnedQuantities?: number;
  returnedFreeQuantities?: number;
  averagePrice?: number;
  averageTaxPrice?: number;
  taxPerUnit?: number;
  loadedAmount?: number;
  loadedTaxAmount?: number;
  loadedVAT?: number;
  returnedAmount?: number;
  returnedTaxAmount?: number;
  returnedVAT?: number;
}

export const FinanceNetAmount = (gross: number, returned: number): number =>
  Math.round((gross - returned) * 100) / 100;

export const FinanceQuantitySplit = (
  physicalQuantity: number | null | undefined,
  paidQuantity?: number | null,
  freeQuantity?: number | null,
) => {
  const asNonNegative = (value: number | null | undefined) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  };
  const physical = asNonNegative(physicalQuantity);
  const free =
    freeQuantity !== undefined && freeQuantity !== null
      ? Math.min(physical, asNonNegative(freeQuantity))
      : undefined;
  const paid =
    free !== undefined
      ? physical - free
      : paidQuantity !== undefined && paidQuantity !== null
        ? Math.min(physical, asNonNegative(paidQuantity))
        : physical;

  return {
    physical,
    paid,
    free: free ?? physical - paid,
  };
};

export const FinanceSalesAmounts = (row: FinanceSalesValues) => {
  const loadedQty = row.paidLoadedQuantities ?? Math.max(
    0,
    (row.loadedQuantities || 0) - (row.freePromotionalQuantities || 0),
  );
  const returnedQty = row.totalPaidReturnedQuantities ?? Math.max(
    0,
    (row.totalReturnedQuantities || 0) - (row.returnedFreeQuantities || 0),
  );
  const loadedIncl = row.loadedAmount ?? loadedQty * (row.averagePrice || 0);
  const loadedExcl = row.loadedTaxAmount ?? loadedQty * (row.averageTaxPrice || 0);
  const loadedVat = row.loadedVAT ?? loadedQty * (row.taxPerUnit || 0);
  const returnedIncl = row.returnedAmount ?? returnedQty * (row.averagePrice || 0);
  const returnedExcl = row.returnedTaxAmount ?? returnedQty * (row.averageTaxPrice || 0);
  const returnedVat = row.returnedVAT ?? returnedQty * (row.taxPerUnit || 0);
  return {
    loadedIncl, loadedExcl, loadedVat, returnedIncl, returnedExcl, returnedVat,
    netIncl: FinanceNetAmount(loadedIncl, returnedIncl),
    netExcl: FinanceNetAmount(loadedExcl, returnedExcl),
    netVat: FinanceNetAmount(loadedVat, returnedVat),
  };
};

export interface FinanceOrderValues {
  variant?: { _id?: string };
  quantityType?: number;
  totalQuantities?: number;
  totalAmount?: number;
  totalTaxAmount?: number;
  tax?: number;
  averagePrice?: number;
  averageTaxPrice?: number;
  taxPerUnit?: number;
}

export const ConsolidateFinanceOrders = <Record extends FinanceOrderValues>(rows: Record[]) => {
  const groups = new Map<string, Record & { orderCount: number }>();
  for (const row of rows) {
    const key = `${row.variant?._id || ''}-${row.quantityType}`;
    const previous = groups.get(key);
    if (!previous) groups.set(key, { ...row, orderCount: 1 });
    else {
      previous.totalQuantities = (previous.totalQuantities || 0) + (row.totalQuantities || 0);
      previous.totalAmount = (previous.totalAmount || 0) + (row.totalAmount || 0);
      previous.totalTaxAmount = (previous.totalTaxAmount || 0) + (row.totalTaxAmount || 0);
      previous.tax = (previous.tax || 0) + (row.tax || 0);
      previous.orderCount++;
    }
  }
  return Array.from(groups.values()).map((row) => {
    const quantity = row.totalQuantities || 0;
    const averagePrice = quantity > 0 ? Number(((row.totalAmount || 0) / quantity).toFixed(2)) : (row.averagePrice || 0);
    const averageTaxPrice = quantity > 0 ? Number(((row.totalTaxAmount || 0) / quantity).toFixed(2)) : (row.averageTaxPrice || 0);
    return { ...row, averagePrice, averageTaxPrice, taxPerUnit: FinanceNetAmount(averagePrice, averageTaxPrice) };
  });
};